// ViewReports.js

import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ReportsContext } from '../contexts/ReportsContext';
import { PostsContext } from '../contexts/PostsContext';
import { getPostById, getUserById, markPostAsReviewed, markReportAsReviewed, markReportAsDeleted } from '../utils/dbUtilities';
import moment from 'moment-timezone';
import './ViewReports.css';

const ViewReports = () => {
    const { reports } = useContext(ReportsContext);
    const { updateReportedStatus, deletePostById } = useContext(PostsContext);
    const [reportDetails, setReportDetails] = useState([]);

    useEffect(() => {
        const fetchReportDetails = async () => {
            let details = await Promise.all(reports.map(async (report) => {
                const post = await getPostById(report.postId);
                const user = await getUserById(report.userId);
                const timestamp = report.timestamp ? new Date(report.timestamp).getTime() : null; // Ensure we have a timestamp or null
                return {
                    ...report,
                    postTitle: post ? post.title : 'Title not found',
                    username: user ? user.username : 'Username not found',
                    postLink: post ? `/posts/${report.postId}` : null,
                    reviewed: report.reviewed,
                    postDeleted: report.postDeleted, // Add postDeleted status
                    timestamp, // Add this line to include the timestamp or null if not present
                };
            }));

            // Sort details by timestamp, ensuring those without a valid timestamp are moved to the bottom
            details.sort((a, b) => {
                if (!a.timestamp) return 1; // Move a to the bottom if timestamp is invalid
                if (!b.timestamp) return -1; // Move b to the bottom if timestamp is invalid
                return b.timestamp - a.timestamp; // Sort by descending order
            });

            setReportDetails(details);
        };

        fetchReportDetails();
    }, [reports]);

    const handleMarkAsReviewed = async (postId, reportId) => {
        try {
            await markPostAsReviewed(postId);
            await updateReportedStatus(postId, false);
            await markReportAsReviewed(reportId);

            setReportDetails(prevDetails =>
                prevDetails.map(detail =>
                    detail.id === reportId ? { ...detail, reviewed: true } : detail
                )
            );
        } catch (error) {
            console.error('Error marking post as reviewed:', error);
        }
    };

    const handleDeletePost = async (postId, reportId) => {
        try {
            if (window.confirm("Are you sure you want to delete this post?")) {
                await deletePostById(postId);
                await markReportAsDeleted(reportId); // Mark report as deleted in the database

                setReportDetails(prevDetails =>
                    prevDetails.map(detail =>
                        detail.id === reportId ? { ...detail, postDeleted: true } : detail
                    )
                );
            }
        } catch (error) {
            console.error('Error deleting post:', error);
        }
    };

    const formatTimestamp = (timestamp) => {
        return moment(timestamp).tz('America/New_York').format('YYYY-MM-DD HH:mm:ss');
    };

    const renderText = (text) => {
        return typeof text === 'string' ? text : 'Invalid details';
    };

    const renderReason = (reason) => {
        return typeof reason === 'string' ? reason : 'Invalid reason';
    };

    return (
        <div className="view-reports-container">
            <h2>View Reports</h2>
            <div className="reports-list">
                {reportDetails.map((detail) => (
                    <div key={detail.id} className="report-item">
                        <p>Type: {detail.type}</p>
                        <p>ID: {detail.id}</p>
                        <p>Reported By: {detail.username}</p>
                        <p>Reported At: {formatTimestamp(detail.timestamp)}</p>
                        <p>Reason: {renderReason(detail.reason)}</p>
                        <p>Details: {renderText(detail.text)}</p>
                        {detail.postLink && (
                            <p>Post Title: <Link to={detail.postLink}>{detail.postTitle}</Link></p>
                        )}
                        <div className="report-action">
                            {detail.postDeleted ? (
                                <p>Post Deleted</p>
                            ) : detail.reviewed ? (
                                <p>Post Marked as Reviewed</p>
                            ) : (
                                <>
                                    <button onClick={() => handleMarkAsReviewed(detail.postId, detail.id)}>
                                        Mark as Reviewed
                                    </button>
                                    <button onClick={() => handleDeletePost(detail.postId, detail.id)}>
                                        Delete Post
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ViewReports;

